import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, isApiError } from '@/lib/api';

export type ThemeKey = 'light' | 'dark' | 'winter';

interface ThemeSettings {
  enabledThemes: ThemeKey[];
  defaultTheme: ThemeKey;
}

interface SettingsEnvelope {
  theme?: ThemeSettings;
}

/**
 * Mirrors the backend's DEFAULT_THEME_SETTINGS, and must stay in step with it:
 * this is what the switch and the snowfall render against before (or instead
 * of) the server's answer. It previously omitted `winter`, so a failed settings
 * read made winter unreachable with no way back except a successful refetch.
 */
const FALLBACK: ThemeSettings = {
  enabledThemes: ['light', 'dark', 'winter'],
  defaultTheme: 'dark',
};

function normalize(raw: unknown): ThemeSettings {
  const block = (raw ?? {}) as Partial<ThemeSettings>;
  const known: ThemeKey[] = ['light', 'dark', 'winter'];
  const enabled = Array.isArray(block.enabledThemes)
    ? (block.enabledThemes.filter((t) => known.includes(t)) as ThemeKey[])
    : [];
  // An empty or unknown list would leave the switch with nothing to render, so
  // fall back rather than trusting the payload.
  const list = enabled.length > 0 ? enabled : FALLBACK.enabledThemes;
  const wanted = block.defaultTheme;
  return {
    enabledThemes: list,
    defaultTheme: list.includes(wanted as ThemeKey)
      ? (wanted as ThemeKey)
      : (wanted === 'winter' && list.includes('winter') ? 'winter' : list[0]),
  };
}

/**
 * Which themes the admin has enabled for this storefront.
 *
 * Read from the same public `GET /marketplace/settings` the rest of the shell
 * uses, so there is no extra request and no admin-only endpoint to guard.
 * A failed read keeps the user-visible default rather than blocking the page.
 */
export function useThemeSettings() {
  const [settings, setSettings] = useState<ThemeSettings>(FALLBACK);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get<SettingsEnvelope>('/marketplace/settings')
      .then((res) => {
        if (cancelled) return;
        if (!isApiError(res)) setSettings(normalize(res.data?.theme));
      })
      .catch(() => {
        /* keep FALLBACK */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => ({ ...settings, loading }), [settings, loading]);
}

/** Admin mutation — PATCH is authenticated, so callers must be signed in. */
export function useSaveThemeSettings() {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useCallback(async (next: ThemeSettings) => {
    setSaving(true);
    setError(null);
    try {
      const res = await api.patch<{ theme?: ThemeSettings }>(
        '/admin/marketplace/settings',
        { theme: next },
      );
      if (isApiError(res)) {
        const msg =
          (res as { message?: string }).message ||
          'Could not save theme settings';
        setError(msg);
        return false;
      }
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save theme settings');
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return { save, saving, error };
}